import { ProcessStep, Testimonial, PricingPlan } from './types';
import { PLAN_CONFIGS } from '../../config/plans';

/**
 * Main features showcase data
 * Used in the "Why smart businesses choose Invirtus" section
 */
export const features = [
  {
    id: "instant-prescreening",
    title: "Instant <br /> Pre-Screening",
    description: "Upload job details and CVs, our AI screens and ranks the top matches by role-fit score in seconds."
  },
  {
  id: "skill-assessments",
  title: "Skill-Based Interviews <br /> By AI",
  description: "Our AI tests problem-solving and job-specific skills with adaptive questions tailored to each CV."
  },
  {
    id: "fit-soft-skills",
    title: "Fit & Soft <br /> Skills Scan",
    description: "AI video reads communication, adaptability, and cultural fit, with signals tied to stronger team alignment."
  },
  {
    id: "meta-cv",
    title: "Meta CV With <br /> Data-Rich Profiles",
    description: "We transform traditional CVs into dynamic, data-rich profiles enriched by behavioral and technical insights."
  }
];

/**
 * Process steps data for "How it works" section
 * Each step explains a part of the Invirtus workflow
 */
export const processSteps: ProcessStep[] = [
  {
    title: "Begin by posting your <br /> job details and CVs",
    description: "Upload the role, required skills, and CVs, our AI handles screening and pre-selection instantly.",
    features: [
      { text: "On Demand Support" },
      { text: "Data Protection" }
    ],
    imageSrc: "/landing-img/cv-upload.png",
    imageAlt: "Job posting and CV upload interface",
    imagePosition: 'right' as const
  },
  {
    title: "Review your shortlist of pre-screened candidates",
    description: "Invirtus provides a ranked list by match score. Candidates then complete AI-driven assessments at their own pace.",
    features: [
      { text: "Hassle-Free" },
      { text: "Targeted List" }
    ],
    imageSrc: "/landing-img/candidates-dashboard.png",
    imageAlt: "Pre-screened candidates dashboard",
    imagePosition: 'left' as const,
    mobileImagePosition: 'right' as const
  },
  {
    title: "Access full candidate reports",
    description: "Review meta-CV profiles, skill-stack results, soft-skills scores, and short video interview highlights before making your decision.",
    features: [
      { text: "Detailed Test Results" },
      { text: "Short Video" }
    ],
    imageSrc: "/landing-img/candidate-profile.png",
    imageAlt: "Detailed candidate profile interface",
    imagePosition: 'right' as const
  }
];

/**
 * Customer testimonials data
 * Used in the testimonials carousel section
 */
export const testimonials: Testimonial[] = [
  {
    id: 1,
    name: "Yann Dulize",
    title: "CEO",
    company: "VIOO",
    quote: "Invirtus has been a true growth accelerator, we landed our first 4 hires in just a few weeks.",
    logoUrl: "/logo/vioo-logo.svg",
    logoAlt: "VIOO company logo",
    rating: 5
  },
  {
    id: 2,
    name: "Simo Bernoussi",
    title: "COO",
    company: "Talaty",
    quote: "We finally process 100% of applications and never miss a great profile. A real game changer for us.",
    logoUrl: "/logo/talaty-logo.png",
    logoAlt: "Talaty company logo",
    rating: 5
  },
  {
    id: 3,
    name: "Issam",
    title: "CTO",
    company: "34 Ventures",
    quote: "A major force multiplier. Invirtus moves our startups from vision to a full tech team with incredible speed.",
    logoUrl: "/logo/34ventures-logo.png",
    logoAlt: "34 Ventures company logo",
    rating: 5
  },
  {
    id: 4,
    name: "Youssef Bouyakhf",
    title: "CEO",
    company: "DeepEcho",
    quote: "Hiring data scientists and engineers for a medtech product is brutal. Invirtus surfaces signal we used to spend weeks digging for.",
    logoUrl: "/logo/deepecho-logo.png",
    logoAlt: "DeepEcho company logo",
    rating: 5
  }
];

/**
 * Data protection and customer support features
 * Used in the data protection section
 */
export const dataProtectionFeatures = [
  {
    id: 1,
    title: "Our team shares values of success",
    description: "At Invirtus, our team is focused on working with you to achieve success.",
    iconId: "success-values"
  },
  {
    id: 2,
    title: "We're on a mission to power our customers",
    description: "Whatever your business needs, we're here to help.",
    iconId: "mission"
  },
  {
    id: 3,
    title: "We're here to protect your data online",
    description: "Your candidate data is handled to recognised security and privacy standards : SOC 2 , HIPAA, and GDPR.",
    iconId: "data-protection"
  }
];

/**
 * Pricing plans configuration — derived from the centralised plan config.
 * Import from '../../config/plans' for the canonical definitions.
 */
export const pricingPlans: PricingPlan[] = PLAN_CONFIGS
  .filter((p) => p.packCode !== 'enterprise') // enterprise is rendered separately
  .map((p) => ({
    planName: p.planName,
    baseAmount: p.baseAmount,
    baseCurrency: p.baseCurrency,
    eurAmount: p.eurAmount,
    priceSubtext: p.priceSubtext,
    description: p.description,
    buttonText: p.buttonText,
    buttonVariant: p.buttonVariant,
    isHighlighted: p.isHighlighted,
    features: p.features,
    packCode: p.packCode,
    hiresPerYear: p.hiresPerYear,
    capabilities: p.capabilities,
  }));

/**
 * Configuration for AI-powered interview analysis tabs
 * Each tab represents a different type of analysis offered by Invirtus
 */
export const tabs = [
  {
    id: "voice",
    title: "Voice Analysis",
    description: "Reveal confidence and clarity in every spoken interaction.",
    contentId: "voice-analysis"
  },
  {
    id: "face",
    title: "Face Analysis",
    description: "Decode micro-expressions to uncover true engagement and fit.",
    contentId: "face-analysis"
  },
  {
    id: "code",
    title: "Coding Review",
    description: "Go beyond answers to highlight logic and coding mastery.",
    contentId: "code-graph"
  },
  {
    id: "all",
    title: "360° Talent View",
    description: "Unlock a complete picture of skills, mindset, and potential.",
    contentId: "all-analysis"
  }
];
